<template>
  <multiselect
    id="ajax"
    ref="contactMultiselect"
    v-model="contactData"
    class="multi-scoped"
    label="name"
    track-by="id"
    placeholder="Type to search contacts and tags"
    open-direction="bottom"
    :disabled="isDisabled"
    :options="availableContactsTags"
    :multiple="true"
    :searchable="true"
    :internal-search="false"
    group-values="values"
    group-label="label"
    :group-select="false"
    :loading="activeSearches > 0"
    :clear-on-select="true"
    :close-on-select="false"
    :options-limit="300"
    :max-height="600"
    :show-no-results="false"
    :hide-selected="true"
    :show-labels="false"
    :class="{ 'hide-options': !loadMoreActive && activeSearches > 0 }"
    @search-change="findContactTag"
    @close="reset"
    @input="inputChange">
    <!-- noOption slot would be better solution but it shows instantly before the request for data is even made. -->
    <template
      v-if="availableContactsTags.length === 0"
      slot="beforeList">
      <li class="multiselect__element">
        <span
          v-if="searchQuery !== ''"
          class="multiselect__option">
          No results found. Try changing the search query.</span>
        <span
          v-else
          class="multiselect__option">
          There are no contacts or tags to show.</span>
      </li>
    </template>
  </multiselect>
</template>

<!-- STYLE --------------------------------------------------------------------------------------------------------------------------------------->
<style lang="scss" scoped>
// @import "../../../../assets/css/libs/multiselect.scss";
.multi-scoped::v-deep .multiselect__tags {
  border: 1px solid $inputBorderColor;
}

// Multiselect has option to not show no results slot but it doesn't have the option to prevent showing the no option slot.
// Since we are initiating a search on debounce it is not a good thing to show no option message right after the user clears the search query.
// Message should be shown after the search request has been finished with empty response. That is why beforeList slot is provided to multiselect.
.multi-scoped::v-deep .multiselect__content-wrapper li:last-of-type {
  display: none!important;
}
</style>

<!-- CONTROLLER ---------------------------------------------------------------------------------------------------------------------------------->
<script>
import { mapGetters, mapActions } from 'vuex';
import Multiselect from 'vue-multiselect';
import { debounce } from 'debounce';

export default {
  name: 'ContactSearch',
  components: {
    Multiselect,
  },
  props: {
    // Indicates that a component should be disabled and hold a preset value. Value is preset using the setContact method called from a parent component.
    // Also, none of the additional requests will be made because ther is no need for them since the value is not changable.
    fixed: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isDisabled: false,
      availableContactsTags: [], // {name, id, type}
      last10Conversations: [],
      contactsList: [],
      tagsList: [],
      activeSearches: 0,
      searchQuery: '',
      loadMoreActive: false,
      contactData: [],
      recentConversations: {
        label: 'Most recently contacted numbers',
        values: [],
      },
      availableTags: {
        label: 'Tags',
        values: [],
      },
      availableContacts: {
        label: 'Contacts',
        values: [],
      },
    };
  },
  computed: {
    ...mapGetters({
      conversationsLoad: 'chat/conversationsLoad',
      conversationList: 'chat/conversationList',
      contacts: 'contact/contactList',
      contactTags: 'contact/contactTagList',
      currentOrganisation: 'organisation/currentOrganisation',
    }),
  },
  watch: {
    conversationsLoad(load) {
      if (load.loaded) {
        this.activeSearches--;
        this.last10Conversations = this.conversationList.data;
        this.recentConversations.values = this.last10Conversations.map(conversation => ({
          name: conversation.contact.name ? `${conversation.contact.name} (${conversation.contact.phone})` : conversation.contact.phone,
          id: conversation.contact.id,
          type: 'contact',
        }));

        // If there are no conversations to load then load unfiltered contacts and tags
        if (this.conversationList.data.length === 0) {
          this.searchForContact(true);
          this.searchForTags();
        }

        this.rebuildMultiselectList();
      }
    },
    contactTags() {
      this.activeSearches--;
      this.availableTags.values = this.contactTags.map(tag => ({
        name: `${tag.name} (${tag.count} ${tag.count !== 1 ? 'contacts' : 'contact'})`,
        id: tag.id,
        type: 'tag',
      }));

      this.rebuildMultiselectList();
    },
    contacts() {
      this.activeSearches--;
      const newContacts = this.contacts.data.map(contact => ({
        name: contact.name ? `${contact.name} (${contact.phone})` : contact.phone,
        id: contact.id,
        type: 'contact',
      }));
      this.contactsList = [...this.contactsList, ...newContacts];
      this.availableContacts.values = this.contactsList;

      this.rebuildMultiselectList();
    },
  },
  mounted() {
    if (this.fixed) {
      this.isDisabled = true;
    } else {
      this.loadInitial10();
    }
    this.$refs.contactMultiselect.$refs.list.addEventListener('scroll', () => this.scrollHandle());
  },
  beforeDestroy() {
    this.$refs.contactMultiselect.$refs.list.removeEventListener('scroll', () => this.scrollHandle());
  },
  methods: {
    ...mapActions({
      fetchConversations: 'chat/fetchConversations',
      fetchContacts: 'contact/fetchContacts',
      fetchContactTags: 'contact/fetchContactTags',
    }),
    loadInitial10() {
      this.activeSearches++;
      const payload = {
        merchant_id: this.$route.params.merchant_id,
        filters: {
          page: 1,
          limit: 10,
          status: 'subscribed',
          channel: 'whatsapp',
        },
      };
      // TODO fetchConversationsForInbox
      this.fetchConversations(payload);
    },
    setContact(contact) {
      this.contactData.push({
        name: contact.name ? `${contact.name} (${contact.phone})` : contact.phone,
        id: contact.id,
        type: 'contact',
      });
      this.inputChange(this.contactData);
    },
    setTag(tag) {
      this.contactData.push({
        name: `${tag.name} (${tag.count} ${tag.count !== 1 ? 'contacts' : 'contact'})`,
        id: tag.id,
        type: 'tag',
      });
      this.inputChange(this.contactData);
    },
    findContactTag: debounce(function f(searchQuery) {
      this.searchQuery = searchQuery;
      // Don't initiate the search request when searchQuery is changed(cleared) on multiselect close event.
      if (!this.$refs.contactMultiselect.isOpen) {
        return;
      }
      // When search query changes, reset the paging and contactList
      this.contactsList = [];
      this.searchForContact(true);
      this.searchForTags();
    }, '300'),
    searchForContact(reset = false) {
      this.activeSearches++;
      let currentPage = this.contacts.meta.current_page + 1;
      if (reset) {
        currentPage = 1;
      }
      this.fetchContacts({
        organisationId: this.currentOrganisation.id,
        data: {
          limit: 10,
          page: currentPage,
          search_query: this.searchQuery,
          has_messaging_consent: true,
          channel: 'whatsapp',
        },
      });
    },
    searchForTags() {
      this.activeSearches++;
      this.fetchContactTags({
        merchantId: this.$route.params.merchant_id,
        data: {
          name: this.searchQuery,
        },
      });
    },
    rebuildMultiselectList() {
      this.availableContactsTags = [];
      // Only show 10 recent conversations if search query is not provided
      if (this.searchQuery === '') {
        if (this.conversationList.data.length === 0) {
          this.addContactsAndTagsToDropdown();
        } else {
          this.availableContactsTags.push(this.recentConversations);
        }
      } else {
        this.addContactsAndTagsToDropdown();
      }
    },
    addContactsAndTagsToDropdown() {
      if (this.availableTags.values.length > 0) {
        this.availableContactsTags.push(this.availableTags);
      }
      if (this.availableContacts.values.length > 0) {
        this.availableContactsTags.push(this.availableContacts);
      }
    },
    scrollHandle() {
      const scrollSize = this.$refs.contactMultiselect.$refs.list.scrollHeight - this.$refs.contactMultiselect.$refs.list.clientHeight;
      const scrollTop = this.$refs.contactMultiselect.$refs.list.scrollTop;
      if (scrollTop === scrollSize) {
        this.$emit('bottom', true);
        this.loadMore();
      }
      if (scrollTop === 0) {
        this.$emit('top', true);
      }
    },
    loadMore() {
      this.loadMoreActive = true;
      if (this.contacts.meta.current_page < this.contacts.meta.last_page) {
        this.searchForContact();
      }
    },
    reset() {
      this.searchQuery = '';
      this.loadMoreActive = false;
      this.rebuildMultiselectList();
    },
    inputChange(values) {
      this.loadMoreActive = false;
      const bulkValues = {
        tags: [],
        contacts: [],
      };
      values.forEach((value) => {
        if (value.type === 'tag') {
          bulkValues.tags.push(value.id);
        } else if (value.type === 'contact') {
          bulkValues.contacts.push(value.id);
        }
      });
      this.$emit('input', bulkValues);
    },
  },
};
</script>
